import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, useQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useConfirm, useDiscardGuard } from '@/components/ui/confirm-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus,
  Layers,
  FileQuestion,
  Send,
  Trash2,
  Edit,
  X,
  ChevronUp,
  ChevronDown,
  GripVertical,
  FileText,
  Video,
  Music,
  Image,
  Link as LinkIcon,
  FileQuestion as QuizIcon,
  CheckCircle2,
  Circle,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Permission } from '@/common/enums/permissions.enum';
import {
  certificationManageService,
  CreateCourseDto,
} from '@/features/certification/services/certification-manage.service';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import '@/components/ui/rich-text-editor.css';

export const Route = createFileRoute(
  '/_authenticated/certification/manage/',
)({
  component: CertificationManagePage,
});

function CertificationManagePage() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.CERTIFICATION_MANAGE);
  const [createOpen, setCreateOpen] = useState(false);
  const [editCourseId, setEditCourseId] = useState<string | null>(null);
  const [stagesModalCourseId, setStagesModalCourseId] = useState<string | null>(
    null,
  );
  const [simulatorModalCourseId, setSimulatorModalCourseId] = useState<
    string | null
  >(null);
  const [contentModalStageId, setContentModalStageId] = useState<string | null>(
    null,
  );
  const [contentModalStageTitle, setContentModalStageTitle] = useState<
    string | undefined
  >(undefined);
  const [creatingQuestionForSimulator, setCreatingQuestionForSimulator] = useState<{
    courseId: string;
  } | null>(null);

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ['certification', 'manage', 'courses'],
    queryFn: () => certificationManageService.listCourses(),
    enabled: canManage,
  });

  const createMutation = useMutation({
    mutationFn: (dto: CreateCourseDto) =>
      certificationManageService.createCourse(dto),
    onSuccess: (course) => {
      queryClient.invalidateQueries({ queryKey: ['certification', 'manage', 'courses'] });
      // Após criar, abrir edição para permitir upload de imagens
      if (course?.id) {
        setCreateOpen(false);
        setTimeout(() => setEditCourseId(course.id), 100);
      } else {
        setCreateOpen(false);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: CreateCourseDto }) =>
      certificationManageService.updateCourse(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certification', 'manage', 'courses'] });
      setEditCourseId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => certificationManageService.deleteCourse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certification', 'manage', 'courses'] });
    },
  });

  const publishMutation = useMutation({
    mutationFn: (id: string) => certificationManageService.publishCourse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certification', 'manage', 'courses'] });
    },
  });

  if (!canManage) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">
          Você não tem permissão para gerenciar cursos.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Gestão de Cursos</h1>
          <p className="text-muted-foreground">
            Criar e editar cursos de certificação.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Criar Curso
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Cursos</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Carregando...</p>
          ) : courses.length === 0 ? (
            <p className="text-muted-foreground">Nenhum curso cadastrado.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Título</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Carga (h)</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {courses.map((course: any) => (
                  <TableRow key={course.id}>
                    <TableCell className="font-medium">{course.title}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          course.status === 'published' ? 'default' : 'secondary'
                        }
                      >
                        {course.status === 'published' ? 'Publicado' : 'Rascunho'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {course.estimatedHours != null
                        ? `${course.estimatedHours}h`
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditCourseId(course.id)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setStagesModalCourseId(course.id)}
                      >
                        <Layers className="h-4 w-4 mr-1" />
                        Etapas
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSimulatorModalCourseId(course.id)}
                      >
                        <FileQuestion className="h-4 w-4 mr-1" />
                        Simulado
                      </Button>
                      {course.status === 'draft' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => publishMutation.mutate(course.id)}
                          disabled={publishMutation.isPending}
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Publicar
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label={`Excluir curso ${course.title}`}
                        onClick={async () => {
                          if (
                            await confirm({
                              title: `Excluir o curso "${course.title}"?`,
                              description: 'Esta ação não pode ser desfeita.',
                              confirmLabel: 'Excluir curso',
                              destructive: true,
                            })
                          ) {
                            deleteMutation.mutate(course.id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateCourseDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={(dto) => createMutation.mutate(dto)}
        isPending={createMutation.isPending}
      />

      {editCourseId && (
        <EditCourseDialog
          courseId={editCourseId}
          open={!!editCourseId}
          onOpenChange={(open) => !open && setEditCourseId(null)}
          onSubmit={(dto) =>
            updateMutation.mutate({ id: editCourseId, dto })
          }
          isPending={updateMutation.isPending}
        />
      )}

      {stagesModalCourseId && (
        <StagesModal
          courseId={stagesModalCourseId}
          onClose={() => setStagesModalCourseId(null)}
          onOpenContent={(stageId, stageTitle) => {
            setContentModalStageId(stageId);
            setContentModalStageTitle(stageTitle);
          }}
        />
      )}

      {simulatorModalCourseId && (
        <>
          {!creatingQuestionForSimulator && (
            <SimulatorConfigModal
              courseId={simulatorModalCourseId}
              onClose={() => setSimulatorModalCourseId(null)}
          onCreateQuestion={(courseId) => {
            setCreatingQuestionForSimulator({ courseId });
          }}
            />
          )}
      {creatingQuestionForSimulator && (
        <CreateQuestionForSimulatorModal
          courseId={creatingQuestionForSimulator.courseId}
          onClose={() => {
            setCreatingQuestionForSimulator(null);
          }}
        />
      )}
        </>
      )}

      {contentModalStageId && (
        <StageContentModal
          stageId={contentModalStageId}
          stageTitle={contentModalStageTitle}
          onClose={() => {
            setContentModalStageId(null);
            setContentModalStageTitle(undefined);
          }}
        />
      )}
    </div>
  );
}

function CreateCourseDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (dto: CreateCourseDto) => void;
  isPending: boolean;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [estimatedHours, setEstimatedHours] = useState<number | ''>('');

  useEffect(() => {
    if (!open) {
      setTitle('');
      setDescription('');
      setEstimatedHours('');
    }
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      title,
      description: description || undefined,
      estimatedHours:
        estimatedHours === '' ? undefined : Number(estimatedHours),
      status: 'draft',
    });
  };

  const guardedOpenChange = useDiscardGuard(
    !!(title.trim() || description.trim() || estimatedHours !== ''),
    onOpenChange,
  );

  return (
    <Dialog open={open} onOpenChange={guardedOpenChange}>
      <DialogContent size="lg" busy={isPending}>
        <DialogHeader>
          <DialogTitle>Criar Curso</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1"
              rows={3}
            />
          </div>
          <div>
            <Label htmlFor="hours">Carga horária (horas)</Label>
            <Input
              id="hours"
              type="number"
              min={0}
              value={estimatedHours}
              onChange={(e) =>
                setEstimatedHours(
                  e.target.value === '' ? '' : parseInt(e.target.value, 10),
                )
              }
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => guardedOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" loading={isPending} disabled={!title.trim()}>
              Criar curso
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditCourseDialog({
  courseId,
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  courseId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (dto: CreateCourseDto) => void;
  isPending: boolean;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [estimatedHours, setEstimatedHours] = useState<number | ''>('');
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);

  const { data: course, isLoading } = useQuery({
    queryKey: ['certification', 'manage', 'course', courseId],
    queryFn: () => certificationManageService.getCourse(courseId),
    enabled: open && !!courseId,
  });

  useEffect(() => {
    if (course) {
      setTitle(course.title || '');
      setDescription(course.description || '');
      setEstimatedHours(course.estimatedHours ?? '');
      // Para preview, usar URL se disponível, senão null (usuário pode fazer upload)
      setCoverPreview(course.coverUrl || null);
      setBannerPreview(course.bannerUrl || null);
    } else if (!open) {
      // Reset ao fechar
      setTitle('');
      setDescription('');
      setEstimatedHours('');
      setCoverPreview(null);
      setBannerPreview(null);
    }
    // Só ressincroniza quando muda o curso/abertura; refetch após upload não pode sobrescrever a edição.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course?.id, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      title,
      description: description || undefined,
      estimatedHours:
        estimatedHours === '' ? undefined : Number(estimatedHours),
    });
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingCover(true);
    try {
      const response = await certificationManageService.uploadCover(
        courseId,
        file,
      );
      // Usar URL assinada retornada pelo backend
      setCoverPreview(response.url || null);
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'course', courseId],
      });
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'courses'],
      });
      toast({
        title: 'Sucesso',
        description: 'Capa enviada com sucesso',
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Erro ao enviar capa',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingCover(false);
    }
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingBanner(true);
    try {
      const response = await certificationManageService.uploadBanner(
        courseId,
        file,
      );
      // Usar URL assinada retornada pelo backend
      setBannerPreview(response.url || null);
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'course', courseId],
      });
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'courses'],
      });
      toast({
        title: 'Sucesso',
        description: 'Banner enviado com sucesso',
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Erro ao enviar banner',
        variant: 'destructive',
      });
    } finally {
      setIsUploadingBanner(false);
    }
  };

  const guardedOpenChange = useDiscardGuard(
    !!course &&
      (title !== (course.title || '') ||
        description !== (course.description || '') ||
        estimatedHours !== (course.estimatedHours ?? '')),
    onOpenChange,
  );

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <p className="text-muted-foreground">Carregando...</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={guardedOpenChange}>
      <DialogContent size="lg" busy={isPending || isUploadingCover || isUploadingBanner}>
        <DialogHeader>
          <DialogTitle>Editar Curso</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="edit-title">Título *</Label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="edit-description">Descrição</Label>
            <Textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1"
              rows={3}
            />
          </div>
          <div>
            <Label htmlFor="edit-hours">Carga horária (horas)</Label>
            <Input
              id="edit-hours"
              type="number"
              min={0}
              value={estimatedHours}
              onChange={(e) =>
                setEstimatedHours(
                  e.target.value === '' ? '' : parseInt(e.target.value, 10),
                )
              }
              className="mt-1"
            />
          </div>

          <div className="space-y-2">
            <Label>Capa do curso</Label>
            {coverPreview ? (
              <div className="relative">
                <img
                  src={coverPreview}
                  alt="Capa"
                  className="w-full rounded-lg object-cover max-h-48"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2"
                  aria-label="Remover capa"
                  onClick={() => {
                    setCoverPreview(null);
                    // TODO: Adicionar endpoint para remover capa se necessário
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverUpload}
                  disabled={isUploadingCover}
                />
                {isUploadingCover && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Enviando...
                  </p>
                )}
                {course?.coverStorageKey && !coverPreview && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Capa já existe. Faça upload de uma nova para substituir.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Banner interno</Label>
            {bannerPreview ? (
              <div className="relative">
                <img
                  src={bannerPreview}
                  alt="Banner"
                  className="w-full rounded-lg object-cover max-h-48"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2"
                  aria-label="Remover banner"
                  onClick={() => {
                    setBannerPreview(null);
                    // TODO: Adicionar endpoint para remover banner se necessário
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleBannerUpload}
                  disabled={isUploadingBanner}
                />
                {isUploadingBanner && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Enviando...
                  </p>
                )}
                {course?.bannerStorageKey && !bannerPreview && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Banner já existe. Faça upload de um novo para substituir.
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => guardedOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" loading={isPending} disabled={!title.trim()}>
              Salvar alterações
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StagesModal({
  courseId,
  onClose,
  onOpenContent,
}: {
  courseId: string;
  onClose: () => void;
  onOpenContent?: (stageId: string, stageTitle: string) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [newTitle, setNewTitle] = useState('');
  const [editingStageId, setEditingStageId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');

  const { data: stages = [], isLoading } = useQuery({
    queryKey: ['certification', 'manage', 'stages', courseId],
    queryFn: () => certificationManageService.listStages(courseId),
  });

  const createMutation = useMutation({
    mutationFn: (title: string) =>
      certificationManageService.createStage(courseId, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'stages', courseId],
      });
      setNewTitle('');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ stageId, title }: { stageId: string; title: string }) =>
      certificationManageService.updateStage(stageId, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'stages', courseId],
      });
      setEditingStageId(null);
      setEditTitle('');
      toast({ title: 'Etapa atualizada com sucesso' });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (stageIds: string[]) =>
      certificationManageService.reorderStages(courseId, stageIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'stages', courseId],
      });
      toast({ title: 'Ordem atualizada' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (stageId: string) =>
      certificationManageService.deleteStage(stageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'stages', courseId],
      });
      toast({ title: 'Etapa removida' });
    },
  });

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newOrder = [...stages];
    [newOrder[index - 1], newOrder[index]] = [
      newOrder[index],
      newOrder[index - 1],
    ];
    reorderMutation.mutate(newOrder.map((s: any) => s.id));
  };

  const handleMoveDown = (index: number) => {
    if (index === stages.length - 1) return;
    const newOrder = [...stages];
    [newOrder[index], newOrder[index + 1]] = [
      newOrder[index + 1],
      newOrder[index],
    ];
    reorderMutation.mutate(newOrder.map((s: any) => s.id));
  };

  const handleStartEdit = (stage: any) => {
    setEditingStageId(stage.id);
    setEditTitle(stage.title);
  };

  const handleSaveEdit = () => {
    if (editingStageId && editTitle.trim()) {
      updateMutation.mutate({ stageId: editingStageId, title: editTitle.trim() });
    }
  };

  const handleCancelEdit = () => {
    setEditingStageId(null);
    setEditTitle('');
  };

  const guardedOpenChange = useDiscardGuard(
    !!newTitle.trim() || editingStageId !== null,
    (open) => !open && onClose(),
  );

  return (
    <Dialog open={!!courseId} onOpenChange={guardedOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Etapas do curso</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Adicione ao menos uma etapa para poder publicar o curso. O conteúdo
            (texto, vídeo, questões) pode ser configurado posteriormente.
          </p>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Título da etapa"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newTitle.trim()) {
                  createMutation.mutate(newTitle.trim());
                }
              }}
            />
            <Button
              onClick={() => {
                if (newTitle.trim()) createMutation.mutate(newTitle.trim());
              }}
              disabled={createMutation.isPending || !newTitle.trim()}
            >
              Adicionar etapa
            </Button>
          </div>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : stages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhuma etapa cadastrada. Adicione uma etapa para começar.
            </p>
          ) : (
            <div className="space-y-2">
              {stages.map((stage: any, idx: number) => (
                <div
                  key={stage.id}
                  className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted/50"
                >
                  <div className="flex items-center gap-1">
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      aria-label="Mover etapa para cima"
                      onClick={() => handleMoveUp(idx)}
                      disabled={idx === 0 || reorderMutation.isPending}
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      aria-label="Mover etapa para baixo"
                      onClick={() => handleMoveDown(idx)}
                      disabled={idx === stages.length - 1 || reorderMutation.isPending}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="flex-1">
                    {editingStageId === stage.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className="h-8"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                          autoFocus
                        />
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={updateMutation.isPending || !editTitle.trim()}
                        >
                          Salvar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelEdit}
                        >
                          Cancelar
                        </Button>
                      </div>
                    ) : (
                      <span className="text-sm font-medium">
                        {idx + 1}. {stage.title}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {editingStageId !== stage.id && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStartEdit(stage)}
                        >
                          Editar
                        </Button>
                        {onOpenContent && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              onOpenContent(stage.id, stage.title);
                              onClose();
                            }}
                          >
                            Conteúdo
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Excluir etapa ${stage.title}`}
                          onClick={async () => {
                            if (
                              await confirm({
                                title: `Excluir a etapa "${stage.title}"?`,
                                description: 'Esta ação não pode ser desfeita.',
                                confirmLabel: 'Excluir etapa',
                                destructive: true,
                              })
                            ) {
                              deleteMutation.mutate(stage.id);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => guardedOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StageContentModal({
  stageId,
  stageTitle,
  onClose,
}: {
  stageId: string;
  stageTitle?: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  const [newlyCreatedBlockId, setNewlyCreatedBlockId] = useState<string | null>(null);

  const { data: blocks = [], isLoading } = useQuery({
    queryKey: ['certification', 'manage', 'blocks', stageId],
    queryFn: () => certificationManageService.listBlocks(stageId),
  });

  // Abrir automaticamente em modo de edição quando um novo bloco é criado
  useEffect(() => {
    if (newlyCreatedBlockId && blocks.some((b: any) => b.id === newlyCreatedBlockId)) {
      setEditingBlockId(newlyCreatedBlockId);
      setNewlyCreatedBlockId(null);
    }
  }, [blocks, newlyCreatedBlockId]);

  const createBlockMutation = useMutation({
    mutationFn: (dto: any) =>
      certificationManageService.createBlock(stageId, dto),
    onSuccess: (newBlock) => {
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'blocks', stageId],
      });
      // Marcar o bloco para abrir em modo de edição após ser carregado
      setNewlyCreatedBlockId(newBlock.id);
      toast({ title: 'Bloco criado com sucesso' });
    },
  });

  const updateBlockMutation = useMutation({
    mutationFn: ({ blockId, dto }: { blockId: string; dto: any }) =>
      certificationManageService.updateBlock(blockId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'blocks', stageId],
      });
      setEditingBlockId(null);
      toast({ title: 'Bloco atualizado' });
    },
  });

  const reorderBlocksMutation = useMutation({
    mutationFn: (blockIds: string[]) =>
      certificationManageService.reorderBlocks(stageId, blockIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'blocks', stageId],
      });
      toast({ title: 'Ordem atualizada' });
    },
  });

  const deleteBlockMutation = useMutation({
    mutationFn: (blockId: string) =>
      certificationManageService.deleteBlock(blockId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'blocks', stageId],
      });
      toast({ title: 'Bloco removido' });
    },
  });

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newOrder = [...blocks];
    [newOrder[index - 1], newOrder[index]] = [
      newOrder[index],
      newOrder[index - 1],
    ];
    reorderBlocksMutation.mutate(newOrder.map((b: any) => b.id));
  };

  const handleMoveDown = (index: number) => {
    if (index === blocks.length - 1) return;
    const newOrder = [...blocks];
    [newOrder[index], newOrder[index + 1]] = [
      newOrder[index + 1],
      newOrder[index],
    ];
    reorderBlocksMutation.mutate(newOrder.map((b: any) => b.id));
  };

  const handleCreateBlock = (type: string) => {
    if (!type) return;
    const defaultContent: Record<string, any> = {};
    if (type === 'text') {
      defaultContent.text = '';
    } else if (type === 'video') {
      defaultContent.url = '';
    } else if (type === 'drive_link') {
      defaultContent.url = '';
    }
    createBlockMutation.mutate({
      type,
      content: Object.keys(defaultContent).length > 0 ? defaultContent : undefined,
    });
  };

  const getBlockTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      text: 'Texto',
      video: 'Vídeo',
      audio: 'Áudio',
      image: 'Imagem',
      drive_link: 'Link do Drive',
      quiz: 'Quiz',
    };
    return labels[type] || type;
  };

  const getBlockTypeIcon = (type: string) => {
    switch (type) {
      case 'text':
        return FileText;
      case 'video':
        return Video;
      case 'audio':
        return Music;
      case 'image':
        return Image;
      case 'drive_link':
        return LinkIcon;
      case 'quiz':
        return QuizIcon;
      default:
        return FileText;
    }
  };

  // Bloco em edição (texto longo, quiz) seria perdido ao fechar
  const guardedOpenChange = useDiscardGuard(
    editingBlockId !== null,
    (open) => !open && onClose(),
  );

  return (
    <Dialog open={!!stageId} onOpenChange={guardedOpenChange}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>
            Conteúdo da etapa: {stageTitle || 'Carregando...'}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Adicione blocos de texto, vídeo, áudio, imagem ou quiz. Use as
            setas para reordenar. Nos blocos de quiz, adicione questões com uma
            opção correta (múltipla escolha ou V/F).
          </p>
        </DialogHeader>
        <div className="space-y-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : blocks.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhum bloco adicionado. Use os botões abaixo para adicionar
              blocos de conteúdo.
            </p>
          ) : (
            <div className="space-y-2">
              {blocks
                .sort((a: any, b: any) => a.order - b.order)
                .map((block: any, idx: number) => {
                  const Icon = getBlockTypeIcon(block.type);
                  const isEditing = editingBlockId === block.id;
                  return (
                    <div
                      key={block.id}
                      className="flex items-start gap-2 p-3 border rounded-lg hover:bg-muted/50"
                    >
                      <div className="flex flex-col items-center gap-1 pt-1">
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          aria-label="Mover bloco para cima"
                          onClick={() => handleMoveUp(idx)}
                          disabled={idx === 0 || reorderBlocksMutation.isPending || isEditing}
                        >
                          <ChevronUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          aria-label="Mover bloco para baixo"
                          onClick={() => handleMoveDown(idx)}
                          disabled={
                            idx === blocks.length - 1 ||
                            reorderBlocksMutation.isPending ||
                            isEditing
                          }
                        >
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm font-medium">
                            {getBlockTypeLabel(block.type)} (ordem {block.order})
                          </span>
                        </div>
                        {isEditing ? (
                          <BlockEditForm
                            block={block}
                            onSave={(dto) => {
                              updateBlockMutation.mutate({
                                blockId: block.id,
                                dto,
                              });
                            }}
                            onCancel={() => setEditingBlockId(null)}
                          />
                        ) : (
                          <BlockPreview block={block} />
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {isEditing ? null : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingBlockId(block.id)}
                            >
                              Editar
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label="Excluir bloco"
                              onClick={async () => {
                                if (
                                  await confirm({
                                    title: 'Excluir este bloco?',
                                    description: 'Esta ação não pode ser desfeita.',
                                    confirmLabel: 'Excluir bloco',
                                    destructive: true,
                                  })
                                ) {
                                  deleteBlockMutation.mutate(block.id);
                                }
                              }}
                              disabled={deleteBlockMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCreateBlock('text')}
              disabled={createBlockMutation.isPending}
            >
              <FileText className="h-4 w-4 mr-2" />
              + Bloco de texto
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCreateBlock('video')}
              disabled={createBlockMutation.isPending}
            >
              <Video className="h-4 w-4 mr-2" />
              + Bloco de vídeo
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCreateBlock('audio')}
              disabled={createBlockMutation.isPending}
            >
              <Music className="h-4 w-4 mr-2" />
              + Bloco de áudio
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCreateBlock('image')}
              disabled={createBlockMutation.isPending}
            >
              <Image className="h-4 w-4 mr-2" />
              + Bloco de imagem
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCreateBlock('drive_link')}
              disabled={createBlockMutation.isPending}
            >
              <LinkIcon className="h-4 w-4 mr-2" />
              + Link do Drive
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCreateBlock('quiz')}
              disabled={createBlockMutation.isPending}
            >
              <QuizIcon className="h-4 w-4 mr-2" />
              + Bloco de quiz
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => guardedOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BlockPreview({ block }: { block: any }) {
  const content = block.content as Record<string, any> | undefined;
  switch (block.type) {
    case 'text': {
      const textContent = content?.text || '';
      if (!textContent || textContent.trim() === '') {
        return (
          <div className="text-sm text-muted-foreground">
            <p className="italic">(Sem conteúdo)</p>
          </div>
        );
      }
      // Renderizar HTML formatado completo, sem truncamento
      return (
        <div className="text-sm text-muted-foreground">
          <div
            className="rich-text-preview-full"
            dangerouslySetInnerHTML={{
              __html: textContent,
            }}
          />
        </div>
      );
    }
    case 'video': {
      const videoPreviewUrl = content?.url || '';
      // Função para converter URL do YouTube para formato de embed
      const convertToEmbedUrlPreview = (url: string): string => {
        if (!url) return '';
        if (url.includes('youtube.com/embed/')) return url;
        const youtubeWatchRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/;
        const youtubeMatch = url.match(youtubeWatchRegex);
        if (youtubeMatch && youtubeMatch[1]) {
          const videoId = youtubeMatch[1].split('&')[0];
          return `https://www.youtube.com/embed/${videoId}`;
        }
        return url;
      };
      const embedPreviewUrl = videoPreviewUrl ? convertToEmbedUrlPreview(videoPreviewUrl) : '';
      return (
        <div className="space-y-2">
          {embedPreviewUrl ? (
            <div className="aspect-video rounded-lg overflow-hidden bg-muted w-full border">
              <iframe
                src={embedPreviewUrl}
                title="Preview do vídeo"
                className="w-full h-full"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              URL: {videoPreviewUrl || '(Não configurado)'}
            </p>
          )}
        </div>
      );
    }
    case 'audio':
      return (
        <p className="text-sm text-muted-foreground">
          {content?.url || content?.storageKey
            ? 'Áudio configurado'
            : '(Não configurado)'}
        </p>
      );
    case 'image': {
      const imagePreviewUrl = content?.url || content?.signedUrl || null;
      if (!imagePreviewUrl) {
        return (
          <p className="text-sm text-muted-foreground italic">
            (Não configurada)
          </p>
        );
      }
      return (
        <div className="relative w-full max-w-xs">
          <img
            src={imagePreviewUrl}
            alt="Preview"
            className="w-full rounded-lg object-cover max-h-32 border"
          />
        </div>
      );
    }
    case 'drive_link':
      return (
        <p className="text-sm text-muted-foreground">
          Link: {content?.url || '(Não configurado)'}
        </p>
      );
    case 'quiz': {
      const quizQuestions = (block as any).questions || [];
      return (
        <div className="text-sm text-muted-foreground">
          <p>Quiz - {quizQuestions.length} questão(ões)</p>
        </div>
      );
    }
    default:
      return null;
  }
}

function BlockEditForm({
  block,
  onSave,
  onCancel,
}: {
  block: any;
  onSave: (dto: any) => void;
  onCancel: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [content, setContent] = useState<Record<string, any>>(
    (block.content as Record<string, any>) || {},
  );
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    kind: 'image' | 'audio',
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const data = await certificationManageService.uploadBlockFile(
        block.id,
        file,
        kind,
      );
      // O backend retorna url ou signedUrl após upload
      const newContent = { 
        ...content, 
        ...data,
        // Garantir que temos uma URL para preview
        url: data.url || data.signedUrl || content?.url,
        signedUrl: data.signedUrl || data.url || content?.signedUrl,
      };
      setContent(newContent);
      // Atualizar o bloco após upload
      onSave({ content: newContent });
      toast({ title: 'Arquivo enviado com sucesso' });
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'blocks'],
      });
    } catch (error: any) {
      console.error('Erro ao fazer upload:', error);
      toast({
        title: 'Erro ao fazer upload',
        description: error?.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  switch (block.type) {
    case 'text':
      return (
        <div className="space-y-2">
          <Label>Conteúdo do bloco de texto</Label>
          <RichTextEditor
            value={content.text || ''}
            onChange={(value) => setContent({ ...content, text: value })}
            placeholder="Digite o conteúdo do bloco..."
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => onSave({ content })}
            >
              Salvar
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
          </div>
        </div>
      );
    case 'video': {
      const videoUrl = content.url || '';
      // Função para converter URL do YouTube para formato de embed
      const convertToEmbedUrl = (url: string): string => {
        if (!url) return '';
        if (url.includes('youtube.com/embed/')) return url;
        const youtubeWatchRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/;
        const youtubeMatch = url.match(youtubeWatchRegex);
        if (youtubeMatch && youtubeMatch[1]) {
          const videoId = youtubeMatch[1].split('&')[0];
          return `https://www.youtube.com/embed/${videoId}`;
        }
        return url;
      };
      const embedUrl = videoUrl ? convertToEmbedUrl(videoUrl) : '';
      return (
        <div className="space-y-2">
          <Label>URL do vídeo (YouTube, etc.)</Label>
          <Input
            value={videoUrl}
            onChange={(e) => setContent({ ...content, url: e.target.value })}
            placeholder="https://www.youtube.com/watch?v=... ou https://youtu.be/..."
          />
          {embedUrl && (
            <div className="aspect-video rounded-lg overflow-hidden bg-muted w-full border">
              <iframe
                src={embedUrl}
                title="Preview do vídeo"
                className="w-full h-full"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onSave({ content })}>
              Salvar
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
          </div>
        </div>
      );
    }
    case 'audio':
      return (
        <div className="space-y-2">
          <Label>Upload de áudio</Label>
          <Input
            type="file"
            accept="audio/*"
            onChange={(e) => handleFileUpload(e, 'audio')}
            disabled={isUploading}
          />
          {isUploading && (
            <p className="text-xs text-muted-foreground">Enviando...</p>
          )}
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
          </div>
        </div>
      );
    case 'image': {
      const imageUrl = content?.url || content?.signedUrl || null;
      return (
        <div className="space-y-2">
          <Label>Upload de imagem</Label>
          {imageUrl && (
            <div className="relative w-full max-w-md">
              <img
                src={imageUrl}
                alt="Preview"
                className="w-full rounded-lg object-cover max-h-64 border"
              />
            </div>
          )}
          <Input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileUpload(e, 'image')}
            disabled={isUploading}
          />
          {isUploading && (
            <p className="text-xs text-muted-foreground">Enviando...</p>
          )}
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
          </div>
        </div>
      );
    }
    case 'drive_link':
      return (
        <div className="space-y-2">
          <Label>URL do link do Drive</Label>
          <Input
            value={content.url || ''}
            onChange={(e) => setContent({ ...content, url: e.target.value })}
            placeholder="https://drive.google.com/..."
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onSave({ content })}>
              Salvar
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
          </div>
        </div>
      );
    case 'quiz':
      return (
        <QuizBlockEditor
          block={block}
          onCancel={onCancel}
        />
      );
    default:
      return null;
  }
}

function QuizBlockEditor({
  block,
  onCancel,
}: {
  block: any;
  onSave?: (dto: any) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [questionType, setQuestionType] = useState<'multiple_choice' | 'true_false'>('multiple_choice');
  const [questionStem, setQuestionStem] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [correctIndex, setCorrectIndex] = useState<number | null>(null);
  const [explanation, setExplanation] = useState('');
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);

  const { data: questions = [] } = useQuery({
    queryKey: ['certification', 'manage', 'questions', block.id],
    queryFn: () => certificationManageService.listQuestionsByBlock(block.id),
  });

  const createQuestionMutation = useMutation({
    mutationFn: (dto: any) =>
      certificationManageService.createQuestion({
        ...dto,
        blockId: block.id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'questions', block.id],
      });
      resetForm();
      toast({ title: 'Questão adicionada com sucesso' });
    },
  });

  const updateQuestionMutation = useMutation({
    mutationFn: ({ questionId, dto }: { questionId: string; dto: any }) =>
      certificationManageService.updateQuestion(questionId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'questions', block.id],
      });
      resetForm();
      toast({ title: 'Questão atualizada' });
    },
  });

  const deleteQuestionMutation = useMutation({
    mutationFn: (questionId: string) =>
      certificationManageService.deleteQuestion(questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'questions', block.id],
      });
      toast({ title: 'Questão removida' });
    },
  });

  const resetForm = () => {
    setQuestionStem('');
    setOptions(['', '']);
    setCorrectIndex(null);
    setExplanation('');
    setEditingQuestionId(null);
    setQuestionType('multiple_choice');
  };

  const handleAddOption = () => {
    setOptions([...options, '']);
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleSaveQuestion = () => {
    if (!questionStem.trim()) {
      toast({
        title: 'Erro',
        description: 'Digite a pergunta',
        variant: 'destructive',
      });
      return;
    }

    const finalOptions =
      questionType === 'true_false'
        ? ['Verdadeiro', 'Falso']
        : options.filter((opt) => opt.trim() !== '');

    if (finalOptions.length < 2) {
      toast({
        title: 'Erro',
        description: 'Adicione pelo menos 2 opções',
        variant: 'destructive',
      });
      return;
    }

    if (correctIndex === null || correctIndex < 0 || correctIndex >= finalOptions.length) {
      toast({
        title: 'Erro',
        description: 'Marque a opção correta',
        variant: 'destructive',
      });
      return;
    }

    const dto: any = {
      stem: questionStem.trim(),
      options: finalOptions,
      correctIndex,
      type: questionType,
    };

    if (editingQuestionId) {
      updateQuestionMutation.mutate({ questionId: editingQuestionId, dto });
    } else {
      createQuestionMutation.mutate(dto);
    }
  };

  const handleEditQuestion = (question: any) => {
    setQuestionStem(question.stem);
    setQuestionType(question.type);
    setOptions(question.type === 'true_false' ? ['Verdadeiro', 'Falso'] : question.options);
    setCorrectIndex(question.correctIndex);
    setExplanation(question.explanation || '');
    setEditingQuestionId(question.id);
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  useEffect(() => {
    if (questionType === 'true_false') {
      setOptions(['Verdadeiro', 'Falso']);
      setCorrectIndex((c) => (c !== null && c >= 2 ? null : c));
    }
  }, [questionType]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Questões: {questions.length}</span>
        </div>
      </div>

      {/* Lista de questões existentes */}
      {questions.length > 0 && (
        <div className="space-y-2 border-t pt-4">
          <Label className="text-sm font-medium">Questões adicionadas:</Label>
          {questions.map((q: any, idx: number) => (
            <div
              key={q.id}
              className="p-3 border rounded-lg bg-muted/30 flex items-start justify-between"
            >
              <div className="flex-1">
                <p className="text-sm font-medium mb-1">
                  {idx + 1}. {q.stem}
                </p>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>Tipo: {q.type === 'multiple_choice' ? 'Múltipla escolha' : 'V/F'}</p>
                  <p>Opções: {q.options.join(', ')}</p>
                  <p>Correta: {q.options[q.correctIndex]}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditQuestion(q)}
                >
                  Editar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Excluir questão"
                  onClick={async () => {
                    if (
                      await confirm({
                        title: 'Excluir esta questão?',
                        description: 'Esta ação não pode ser desfeita.',
                        confirmLabel: 'Excluir questão',
                        destructive: true,
                      })
                    ) {
                      deleteQuestionMutation.mutate(q.id);
                    }
                  }}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Formulário de nova/edição de questão */}
      <div className="space-y-4 border-t pt-4">
        <Label className="text-sm font-medium">
          {editingQuestionId ? 'Editar questão' : 'Adicionar nova questão'}
        </Label>

        {/* Tipo de questão */}
        <div className="space-y-2">
          <Label>Tipo</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={questionType === 'multiple_choice' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setQuestionType('multiple_choice')}
              className="flex items-center gap-2"
            >
              {questionType === 'multiple_choice' && <CheckCircle2 className="h-4 w-4" />}
              Múltipla escolha
            </Button>
            <Button
              type="button"
              variant={questionType === 'true_false' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setQuestionType('true_false')}
              className="flex items-center gap-2"
            >
              {questionType === 'true_false' && <CheckCircle2 className="h-4 w-4" />}
              Verdadeiro/Falso
            </Button>
          </div>
        </div>

        {/* Pergunta */}
        <div className="space-y-2">
          <Label>Pergunta</Label>
          <Input
            value={questionStem}
            onChange={(e) => setQuestionStem(e.target.value)}
            placeholder="Pergunta..."
          />
        </div>

        {/* Opções */}
        <div className="space-y-2">
          <Label>
            Opções {questionType === 'multiple_choice' && '(marque 1 correta)'}
          </Label>
          {options.map((option, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                value={option}
                onChange={(e) => handleOptionChange(index, e.target.value)}
                placeholder={`Opção ${index + 1}`}
                disabled={questionType === 'true_false'}
                className="flex-1"
              />
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant={correctIndex === index ? 'default' : 'outline'}
                  size="icon"
                  className="h-9 w-9"
                  aria-label={`Marcar opção ${index + 1} como correta`}
                  onClick={() => setCorrectIndex(index)}
                >
                  {correctIndex === index ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <Circle className="h-4 w-4" />
                  )}
                </Button>
                <span className="text-xs text-muted-foreground">Correta</span>
              </div>
            </div>
          ))}
          {questionType === 'multiple_choice' && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleAddOption}
              className="text-primary"
            >
              <Plus className="h-4 w-4 mr-1" />
              + Outra opção
            </Button>
          )}
        </div>

        {/* Explicação */}
        <div className="space-y-2">
          <Label>Explicação</Label>
          <Textarea
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            placeholder="Explicação após responder..."
            rows={3}
          />
        </div>

        {/* Botões */}
        <div className="flex gap-2">
          {editingQuestionId ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCancelEdit}
                disabled={createQuestionMutation.isPending || updateQuestionMutation.isPending}
              >
                Cancelar edição
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleSaveQuestion}
                disabled={createQuestionMutation.isPending || updateQuestionMutation.isPending}
              >
                Salvar questão
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onCancel}
                disabled={createQuestionMutation.isPending || updateQuestionMutation.isPending}
              >
                Fechar
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleSaveQuestion}
                disabled={createQuestionMutation.isPending || updateQuestionMutation.isPending}
              >
                Adicionar questão
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SimulatorConfigModal({
  courseId,
  onClose,
  onCreateQuestion,
}: {
  courseId: string;
  onClose: () => void;
  onCreateQuestion?: (courseId: string) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();

  const { data: course } = useQuery({
    queryKey: ['certification', 'manage', 'course', courseId],
    queryFn: () => certificationManageService.getCourse(courseId),
    enabled: !!courseId,
  });

  // Buscar stages com blocos para encontrar blocos de quiz
  const { data: stagesWithBlocks = [] } = useQuery({
    queryKey: ['certification', 'manage', 'stages-with-blocks', courseId],
    queryFn: async () => {
      const stages = await certificationManageService.listStages(courseId);
      const stagesWithBlocksData = await Promise.all(
        stages.map(async (stage: any) => {
          const blocks = await certificationManageService.listBlocks(stage.id);
          return { ...stage, blocks };
        }),
      );
      return stagesWithBlocksData;
    },
    enabled: !!courseId,
  });

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['certification', 'manage', 'simulator-config', courseId],
    queryFn: () => certificationManageService.getSimulatorConfig(courseId),
  });

  const { data: poolQuestions = [], isLoading: poolLoading } = useQuery({
    queryKey: ['certification', 'manage', 'pool-questions', courseId],
    queryFn: () => certificationManageService.listPoolQuestions(courseId),
  });

  const [questionsCount, setQuestionsCount] = useState(3);
  useEffect(() => {
    if (config) {
      setQuestionsCount(config.questionsCount ?? 3);
    }
  }, [config]);

  const putMutation = useMutation({
    mutationFn: (dto: {
      questionsCount: number;
      minQuestionsToPublish: number;
    }) => certificationManageService.putSimulatorConfig(courseId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'simulator-config', courseId],
      });
      toast({ title: 'Configuração salva com sucesso' });
    },
  });

  const removeFromPoolMutation = useMutation({
    mutationFn: (questionId: string) =>
      certificationManageService.removeFromPool(courseId, questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'pool-questions', courseId],
      });
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'simulator-config', courseId],
      });
      toast({ title: 'Questão removida do pool' });
    },
  });

  const handleCreateQuestion = async () => {
    // Abrir modal para criar questão do simulado (sem necessidade de bloco de quiz)
    if (onCreateQuestion) {
      onCreateQuestion(courseId);
    }
  };

  const handleSaveConfig = () => {
    putMutation.mutate({
      questionsCount,
      minQuestionsToPublish: config?.minQuestionsToPublish ?? 5,
    });
  };

  const guardedOpenChange = useDiscardGuard(
    !!config && questionsCount !== (config.questionsCount ?? 3),
    (open) => !open && onClose(),
  );

  return (
    <Dialog open={!!courseId} onOpenChange={guardedOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            Configurar simulado: {course?.title || 'Carregando...'}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Defina quantas questões aparecem no simulado e escolha quais questões
            fazem parte do banco. Se houver mais questões no pool do que a
            quantidade definida, serão sorteadas aleatoriamente.
          </p>
        </DialogHeader>
        {configLoading ? (
          <p className="text-sm text-muted-foreground">Carregando...</p>
        ) : (
          <div className="space-y-6">
            {/* Configuração de quantidade */}
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label>Quantidade de questões no simulado</Label>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={questionsCount}
                  onChange={(e) =>
                    setQuestionsCount(parseInt(e.target.value, 10) || 3)
                  }
                  className="mt-1"
                />
              </div>
              <Button
                onClick={handleSaveConfig}
                disabled={putMutation.isPending}
              >
                Salvar
              </Button>
            </div>

            {/* Informações do pool */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-sm">
                <span className="font-medium">Pool:</span> {config?.poolCount ?? 0}{' '}
                questão(ões).{' '}
                <span className="font-medium">Mínimo {config?.minQuestionsToPublish ?? 5} para publicar.</span>
              </p>
            </div>

            {/* Lista de questões do pool */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Questões no pool:</Label>
              {poolLoading ? (
                <p className="text-sm text-muted-foreground">Carregando...</p>
              ) : poolQuestions.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  Nenhuma questão no pool. Adicione questões de blocos de quiz.
                </p>
              ) : (
                <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                  {poolQuestions.map((q: any) => (
                    <div
                      key={q.questionId}
                      className="flex items-center justify-between p-3 hover:bg-muted/50"
                    >
                      <span className="text-sm flex-1">{q.stem}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          if (
                            await confirm({
                              title: 'Remover esta questão do pool do simulado?',
                              confirmLabel: 'Remover questão',
                              destructive: true,
                            })
                          ) {
                            removeFromPoolMutation.mutate(q.questionId);
                          }
                        }}
                        disabled={removeFromPoolMutation.isPending}
                        className="text-destructive hover:text-destructive"
                      >
                        Remover
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Lista de questões disponíveis para adicionar ao pool */}
            {stagesWithBlocks && stagesWithBlocks.length > 0 && (
              <AvailableQuestionsSection
                courseId={courseId}
                stages={stagesWithBlocks}
                poolQuestionIds={poolQuestions.map((q: any) => q.questionId)}
              />
            )}

            {/* Botão para criar nova questão */}
            <div>
              <Button
                variant="outline"
                onClick={handleCreateQuestion}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Criar nova questão para o simulado
              </Button>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => guardedOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AvailableQuestionsSection({
  courseId,
  stages,
  poolQuestionIds,
}: {
  courseId: string;
  stages: any[];
  poolQuestionIds: string[];
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);

  // Buscar questões de todos os blocos de quiz
  const quizBlockIds = stages
    .flatMap((stage: any) => stage.blocks || [])
    .filter((block: any) => block.type === 'quiz')
    .map((block: any) => block.id);

  const questionQueries = useQueries({
    queries: quizBlockIds.map((blockId: string) => ({
      queryKey: ['certification', 'manage', 'questions', blockId],
      queryFn: () => certificationManageService.listQuestionsByBlock(blockId),
      enabled: expanded && quizBlockIds.length > 0,
    })),
  });

  const allQuestions = questionQueries
    .flatMap((query: any) => query.data || [])
    .filter((q: any) => !poolQuestionIds.includes(q.id));

  const addToPoolMutation = useMutation({
    mutationFn: (questionId: string) =>
      certificationManageService.addToPool(courseId, questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'pool-questions', courseId],
      });
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'simulator-config', courseId],
      });
      toast({ title: 'Questão adicionada ao pool' });
    },
  });

  if (quizBlockIds.length === 0) {
    return null;
  }

  const isLoading = questionQueries.some((q: any) => q.isLoading);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">
          Questões disponíveis para adicionar ao pool:
        </Label>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Ocultar' : 'Mostrar'}
        </Button>
      </div>
      {expanded && (
        <>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : allQuestions.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              Todas as questões já estão no pool ou não há questões nos blocos de quiz.
            </p>
          ) : (
            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
              {allQuestions.map((q: any) => (
                <div
                  key={q.id}
                  className="flex items-center justify-between p-3 hover:bg-muted/50"
                >
                  <span className="text-sm flex-1">{q.stem}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => addToPoolMutation.mutate(q.id)}
                    disabled={addToPoolMutation.isPending}
                  >
                    Adicionar ao pool
                  </Button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CreateQuestionForSimulatorModal({
  courseId,
  onClose,
}: {
  courseId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [questionType, setQuestionType] = useState<'multiple_choice' | 'true_false'>('multiple_choice');
  const [questionStem, setQuestionStem] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [correctIndex, setCorrectIndex] = useState<number | null>(null);
  const [explanation, setExplanation] = useState('');

  const createQuestionMutation = useMutation({
    mutationFn: (dto: any) =>
      certificationManageService.createQuestion({
        ...dto,
        courseId, // Apenas courseId, sem blockId (questão do simulado)
      }),
    onSuccess: async (newQuestion) => {
      // Adicionar automaticamente ao pool
      await certificationManageService.addToPool(courseId, newQuestion.id);
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'pool-questions', courseId],
      });
      queryClient.invalidateQueries({
        queryKey: ['certification', 'manage', 'simulator-config', courseId],
      });
      toast({ title: 'Questão criada e adicionada ao pool' });
      onClose();
    },
  });

  const handleAddOption = () => {
    setOptions([...options, '']);
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleSave = () => {
    if (!questionStem.trim()) {
      toast({
        title: 'Erro',
        description: 'Digite a pergunta',
        variant: 'destructive',
      });
      return;
    }

    const finalOptions =
      questionType === 'true_false'
        ? ['Verdadeiro', 'Falso']
        : options.filter((opt) => opt.trim() !== '');

    if (finalOptions.length < 2) {
      toast({
        title: 'Erro',
        description: 'Adicione pelo menos 2 opções',
        variant: 'destructive',
      });
      return;
    }

    if (correctIndex === null || correctIndex < 0 || correctIndex >= finalOptions.length) {
      toast({
        title: 'Erro',
        description: 'Marque a opção correta',
        variant: 'destructive',
      });
      return;
    }

    createQuestionMutation.mutate({
      stem: questionStem.trim(),
      options: finalOptions,
      correctIndex,
      type: questionType,
    });
  };

  useEffect(() => {
    if (questionType === 'true_false') {
      setOptions(['Verdadeiro', 'Falso']);
      setCorrectIndex((c) => (c !== null && c >= 2 ? null : c));
    }
  }, [questionType]);

  const guardedOpenChange = useDiscardGuard(
    !!(questionStem.trim() || explanation.trim() || (questionType === 'multiple_choice' && options.some((o) => o.trim()))),
    (open) => !open && onClose(),
  );

  if (!courseId) return null;

  return (
    <Dialog open={!!courseId} onOpenChange={guardedOpenChange}>
      <DialogContent size="lg" busy={createQuestionMutation.isPending}>
        <DialogHeader>
          <DialogTitle>Criar questão para o simulado</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Tipo */}
          <div className="space-y-2">
            <Label>Tipo</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={questionType === 'multiple_choice' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setQuestionType('multiple_choice')}
              >
                Múltipla escolha
              </Button>
              <Button
                type="button"
                variant={questionType === 'true_false' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setQuestionType('true_false')}
              >
                Verdadeiro/Falso
              </Button>
            </div>
          </div>

          {/* Pergunta */}
          <div className="space-y-2">
            <Label>Pergunta</Label>
            <Input
              value={questionStem}
              onChange={(e) => setQuestionStem(e.target.value)}
              placeholder="Pergunta..."
            />
          </div>

          {/* Opções */}
          <div className="space-y-2">
            <Label>
              Opções {questionType === 'multiple_choice' && '(marque 1 correta)'}
            </Label>
            {options.map((option, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  placeholder={`Opção ${index + 1}`}
                  disabled={questionType === 'true_false'}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant={correctIndex === index ? 'default' : 'outline'}
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => setCorrectIndex(index)}
                >
                  {correctIndex === index ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <Circle className="h-4 w-4" />
                  )}
                </Button>
                <span className="text-xs text-muted-foreground">Correta</span>
              </div>
            ))}
            {questionType === 'multiple_choice' && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAddOption}
                className="text-primary"
              >
                <Plus className="h-4 w-4 mr-1" />
                + Outra opção
              </Button>
            )}
          </div>

          {/* Explicação */}
          <div className="space-y-2">
            <Label>Explicação</Label>
            <Textarea
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder="Explicação após responder..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => guardedOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} loading={createQuestionMutation.isPending}>
            Criar questão
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
